import { baseApi } from './base.api';
import { projectsService, type ProjectIntegration } from './projectsService';
import { PATH_PROJECTS_API } from '../constants/api-routes';
import { getApiVersion } from 'src/utils/apiVersion';

type Id = number | string;

// A Zendesk ticket status the AI can set; agent_label is its name as agents see it.
export interface ZendeskStatusField {
  id: Id;
  agent_label: string;
  [key: string]: unknown;
}

export interface FreshdeskAgent {
  id: number;
  email: string;
  [key: string]: unknown;
}

export interface FreshdeskGroup {
  id: number;
  name: string;
  [key: string]: unknown;
}

export const projectIntegrationsService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      addIntegration: build.mutation({
        query: ({ id, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${id}/${PATH_PROJECTS_API.INTEGRATIONS}`,
            method: 'POST',
            body: body,
          };
        },
      }),
      getProjectIntegrationsList: build.query<
        ProjectIntegration[],
        // skip_dispatch leaves the cached project's integrations untouched.
        { project_id: Id; skip_dispatch?: boolean }
      >({
        query: ({ project_id }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${project_id}/${PATH_PROJECTS_API.INTEGRATIONS}`,
          };
        },
        async onQueryStarted({ project_id, skip_dispatch }, { dispatch, queryFulfilled }) {
          if (skip_dispatch) {
            return;
          }

          try {
            const { data: integrationsList } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${project_id}` },
                (draft) => {
                  draft.integrations = integrationsList;
                },
              ),
            );
          } catch (err) {
            console.error('Error get project integrations:', err);
          }
        },
      }),
      getZendeskConsentLink: build.query({
        query: ({ project_id, integration_id, type = 'zendesk-email' }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${project_id}/${PATH_PROJECTS_API.INTEGRATIONS}/${integration_id}/${type}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_OAUTH_AUTHORIZE_URL}`,
          };
        },
        keepUnusedDataFor: 0,
      }),
      updateIntegration: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}`,
            method: 'PUT',
            body: body,
          };
        },
        async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedIntegration } = await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  if (!draft.integrations) return;

                  const index = draft.integrations.findIndex(
                    (integration) =>
                      integration.integration_id === updatedIntegration.integration_id,
                  );

                  if (index !== -1) {
                    draft.integrations[index] = updatedIntegration;
                  }
                },
              ),
            );
          } catch (err) {
            console.error('Error updating project integration:', err);
          }
        },
      }),
      zendeskEmailIntegrationAutoSetup: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_AUTO_SETUP}`,
            method: 'POST',
            body: {
              ...(body.escalated_assignee_id && {
                escalated_assignee_id: body.escalated_assignee_id,
              }),
              ...(body.escalated_custom_status_id && {
                escalated_custom_status_id: body.escalated_custom_status_id,
              }),
              ...(body.escalated_group_id && {
                escalated_group_id: body.escalated_group_id,
              }),
            },
          };
        },
      }),
      getZendeskEmailStatusFields: build.query<
        ZendeskStatusField[],
        { projectId: Id; integrationId: Id; type?: string }
      >({
        query: ({ projectId, integrationId, type }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_STATUS_FIELDS}`,
            params: {
              ...(type !== '' && { project_type: type }),
            },
          };
        },
      }),
      getZendeskEmailAssigneeFields: build.query({
        query: ({ projectId, integrationId, query }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_ASSIGNEE_FIELDS}${query ? '?' + query : ''}`,
          };
        },
      }),
      getZendeskEmailGroupsFields: build.query({
        query: ({ projectId, integrationId, page }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_EMAIL_GROUPS_FIELDS}`,
            params: {
              page: page,
            },
          };
        },
      }),
      getFreshdeskEmailAgents: build.query<
        FreshdeskAgent[],
        // Callers also pass the project type; the request ignores it, but it is part of the cache key.
        { projectId: Id; integrationId: Id; query?: string; type?: string }
      >({
        query: ({ projectId, integrationId, query }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_FRESHDESK_EMAIL_AGENTS}${query ? '?' + query : ''}`,
          };
        },
      }),
      getFreshdeskEmailGroups: build.query<
        FreshdeskGroup[],
        // Callers also pass the project type; the request ignores it, but it is part of the cache key.
        { projectId: Id; integrationId: Id; query?: string; type?: string }
      >({
        query: ({ projectId, integrationId, query }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_FRESHDESK_EMAIL_GROUPS}${query ? '?' + query : ''}`,
          };
        },
      }),
      freshdeskEmailIntegrationAutoSetup: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_FRESHDESK_EMAIL_AUTO_SETUP}`,
            method: 'POST',
            body: {
              responder_id: body.responder_id,
              responder_email: body.responder_email,
              ...(body.escalate_assign_group_id && {
                escalate_assign_group_id: body.escalate_assign_group_id,
              }),
            },
          };
        },
      }),
      zendeskChatIntegrationAutoSetup: build.mutation({
        query: ({ projectId, integrationId, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZENDESK_CHAT_AUTO_SETUP}`,
            method: 'POST',
            body: {
              widget_integration_id: body.widget_integration_id,
              responder_mode: body.responder_mode || 'default_responder',
            },
          };
        },
        invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectById', id: projectId }],
      }),
      getZendeskChatWidgets: build.query({
        query: ({ projectId, integrationId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.ZENDESK_CHAT_WIDGETS}`,
            params: {
              filter_types: 'web',
            },
          };
        },
      }),
      // Returns the chat widget code to install on the customer's site.
      getZohoSalesIQSetup: build.mutation<
        { plug_code?: string },
        { projectId: Id; integrationId: Id }
      >({
        query: ({ projectId, integrationId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZOHO_SALES_IQ_SETUP}`,
            method: 'POST',
          };
        },
      }),
      getZohoEmailSetup: build.mutation({
        query: ({ projectId, integrationId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}/${PATH_PROJECTS_API.INTEGRATIONS_ZOHO_DESK_SETUP}`,
            method: 'POST',
          };
        },
      }),
      deleteIntegration: build.mutation({
        query: ({ projectId, integrationId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_PROJECTS_API.ROOT}/${projectId}/${PATH_PROJECTS_API.INTEGRATIONS}/${integrationId}`,
            method: 'DELETE',
          };
        },
        async onQueryStarted({ projectId, integrationId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              projectsService.util.updateQueryData(
                'getProjectById',
                { id: `${projectId}` },
                (draft) => {
                  const index = draft.integrations.findIndex(
                    (integration) => integration.integration_id === integrationId,
                  );

                  draft.integrations.splice(index, 1);
                },
              ),
            );
          } catch (err) {
            console.error('Error deleting project integration:', err);
          }
        },
        invalidatesTags: (result, error, { projectId }) => [{ type: 'ProjectById', id: projectId }],
      }),
    };
  },
});

export const {
  useAddIntegrationMutation,
  useGetZendeskConsentLinkQuery,
  useLazyGetZendeskConsentLinkQuery,
  useGetProjectIntegrationsListQuery,
  useLazyGetProjectIntegrationsListQuery,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useZendeskEmailIntegrationAutoSetupMutation,
  useGetFreshdeskEmailAgentsQuery,
  useGetFreshdeskEmailGroupsQuery,
  useFreshdeskEmailIntegrationAutoSetupMutation,
  useGetZendeskEmailStatusFieldsQuery,
  useGetZendeskEmailAssigneeFieldsQuery,
  useGetZendeskEmailGroupsFieldsQuery,
  useZendeskChatIntegrationAutoSetupMutation,
  useGetZendeskChatWidgetsQuery,
  useGetZohoSalesIQSetupMutation,
  useGetZohoEmailSetupMutation,
} = projectIntegrationsService;
